import React, { useMemo } from "react";
import YhInput from "../../ui/YhInput";
import YhSelect from "../../ui/YhSelect";
import { country1 } from "../../utils/country";
import { store } from "../../utils/store";

export type PhoneCountry = {
  label: string;
  code: string;
  dialCode: string;
};

/** Prefer common markets first, then full dial-code list. */
export const PHONE_COUNTRY_OPTIONS: PhoneCountry[] = (() => {
  const preferred = ["IN", "AE", "US", "GB", "SG", "SA"];
  const fromLib = (Array.isArray(country1) ? country1 : []).map((c: any) => ({
    label: String(c.name || c.code),
    code: String(c.code || ""),
    dialCode: String(c.dial_code || ""),
  }));
  const byCode = new Map(fromLib.map((c) => [c.code, c]));
  const head = preferred
    .map((code) => byCode.get(code))
    .filter(Boolean) as PhoneCountry[];
  const rest = fromLib.filter((c) => c.code && !preferred.includes(c.code));
  const merged = [...head, ...rest].filter((c) => c.dialCode && c.code);
  return merged.length
    ? merged
    : [
        { label: "India", code: "IN", dialCode: "+91" },
        { label: "United Arab Emirates", code: "AE", dialCode: "+971" },
        { label: "United States", code: "US", dialCode: "+1" },
      ];
})();

export function findPhoneCountry(
  codeOrDial?: string | null
): PhoneCountry | null {
  const key = String(codeOrDial || "").trim();
  if (!key) return null;
  return (
    PHONE_COUNTRY_OPTIONS.find(
      (c) => c.code === key || c.dialCode === key || c.dialCode === `+${key}`
    ) || null
  );
}

export function defaultPhoneCountry(): PhoneCountry {
  return findPhoneCountry("IN") || PHONE_COUNTRY_OPTIONS[0];
}

/** Digits only, max length for national number entry. */
export function nationalPhoneDigits(value: string, maxLen = 15) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxLen);
}

/**
 * Compose E.164-ish phone: +{dial}{national}.
 * Accepts 4–15 national digits (CP last-4 or full mobile).
 */
export function composePhoneWithCountry(
  dialCode: string,
  nationalNumber: string
): string {
  const digits = nationalPhoneDigits(nationalNumber, 15);
  if (!digits) return "";
  const dialDigits = String(dialCode || "").replace(/\D/g, "") || "91";
  // Avoid double-prefix if user pasted full international number
  if (digits.length >= 10 && digits.startsWith(dialDigits)) {
    return `+${digits}`;
  }
  return `+${dialDigits}${digits}`;
}

function inferDialAndNational(raw: string): { dial: string; national: string } {
  const hasPlus = raw.startsWith("+");
  // Preserve mask chars in national part when already masked
  if (raw.includes("*")) {
    const plusMatch = raw.match(/^\+(\d{1,4})\s*(.*)$/);
    if (plusMatch) {
      return { dial: `+${plusMatch[1]}`, national: plusMatch[2].trim() };
    }
    return { dial: "", national: raw };
  }

  const digits = raw.replace(/\D/g, "");
  if (!digits) return { dial: "", national: raw };

  let dial = "";
  if (hasPlus || digits.length > 10) {
    const sorted = [...PHONE_COUNTRY_OPTIONS].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    );
    for (const c of sorted) {
      const d = c.dialCode.replace(/\D/g, "");
      if (d && digits.startsWith(d)) {
        dial = c.dialCode;
        break;
      }
    }
    // Default India when 12 digits starting with 91 (common CRM storage)
    if (!dial && digits.length >= 12 && digits.startsWith("91")) {
      dial = "+91";
    }
  }
  const national = dial
    ? digits.slice(String(dial).replace(/\D/g, "").length)
    : digits;
  return { dial, national };
}

/**
 * Display helper: show dial code + national digits.
 * Does NOT remask — API already applies org Lead phone masking / allowlists.
 * Full: +91 9876543210 · Masked: +91 ******3210
 */
export function formatPhoneKeepingCountryCode(phone: any): string {
  const raw = String(phone || "").trim();
  if (!raw) return "—";
  if (raw.includes("*")) {
    const { dial, national } = inferDialAndNational(raw);
    return dial ? `${dial} ${national}` : raw.replace(/\s+/g, " ").trim();
  }

  const { dial, national } = inferDialAndNational(raw);
  if (!national) return raw;
  if (dial) return `${dial} ${national}`;
  // Bare 10-digit Indian mobile → show default India code in UI
  if (/^\d{10}$/.test(national)) return `+91 ${national}`;
  return national;
}

/** Prefer full number when API allowlists the viewer; else masked. */
export function resolveLeadPhoneForDisplay(row: any): string {
  if (!row) return "";

  let isOrgAdmin = false;
  try {
    const auth = store.getState()?.auth;
    isOrgAdmin = String(auth?.hierarchyLevel ?? "") === "1";
  } catch {
    isOrgAdmin = false;
  }

  // Explicit deny from API
  if (row.canViewFullPhone === false || row.phoneMasked === true) {
    const masked = String(row.leadPhoneMasked || row.leadPhone || "");
    return masked.includes("*") ? masked : clientMaskPhone(masked);
  }

  // Explicit allow from API (admin or configured allowlist)
  if (row.canViewFullPhone === true) {
    return String(
      row.leadPhoneFull || row.leadPhone || row.leadPhoneMasked || ""
    );
  }

  // No flags: org admin sees as-is; team always masked
  const phone = String(row.leadPhoneMasked || row.leadPhone || "");
  if (!phone) return "";
  if (isOrgAdmin || phone.includes("*")) return phone;
  return clientMaskPhone(phone);
}

/** Client-side mask when API flags are missing (team never sees full digits). */
function clientMaskPhone(value: string, visibleDigits = 4): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (!digits || digits.length <= visibleDigits) return raw;
  const dial = digits.startsWith("91") && digits.length >= 12 ? "91" : "";
  const national = dial ? digits.slice(dial.length) : digits.slice(-10);
  if (national.length <= visibleDigits) {
    return dial ? `+${dial} ${national}` : national;
  }
  const masked =
    "*".repeat(national.length - visibleDigits) + national.slice(-visibleDigits);
  return dial ? `+${dial} ${masked}` : masked;
}

type Props = {
  label?: string;
  countryCode: string;
  phone: string;
  onCountryChange: (code: string, country: PhoneCountry | null) => void;
  onPhoneChange: (nationalDigits: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  phonePlaceholder?: string;
  /** Max national digits (default 15; use 10 for strict mobile). */
  maxDigits?: number;
};

const PhoneWithCountryCode: React.FC<Props> = ({
  label = "Mobile",
  countryCode,
  phone,
  onCountryChange,
  onPhoneChange,
  required,
  disabled,
  error,
  phonePlaceholder = "10-digit or last 4",
  maxDigits = 15,
}) => {
  const options = useMemo(
    () =>
      PHONE_COUNTRY_OPTIONS.map((c) => ({
        label: `${c.label} (${c.dialCode})`,
        value: c.code,
      })),
    []
  );

  return (
    <div className="w-full">
      {label ? (
        <div className="yh-label mb-1">
          {required ? <span className="mr-0.5 text-yh-red">*</span> : null}
          {label}
        </div>
      ) : null}
      <div className="grid grid-cols-[140px_1fr] gap-2">
        <YhSelect
          label="Code"
          placeholder="Code"
          searchable
          searchPlaceholder="Search country..."
          showOptionIcon={false}
          disabled={disabled}
          value={countryCode || "IN"}
          options={options}
          onChange={(e) => {
            const code = String(e?.target?.value || "");
            onCountryChange(code, findPhoneCountry(code));
          }}
        />
        <YhInput
          label="Number"
          placeholder={phonePlaceholder}
          value={phone}
          disabled={disabled}
          error={error}
          inputMode="numeric"
          maxLength={maxDigits}
          onChange={(e: any) =>
            onPhoneChange(nationalPhoneDigits(e.target.value, maxDigits))
          }
        />
      </div>
    </div>
  );
};

export default PhoneWithCountryCode;
